import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator, TextInput, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

const functionsUrl = typeof __DEV__ !== 'undefined' && __DEV__
  ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
  : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const COLORS = {
  bg: '#0A0E1A',
  card: '#141926',
  cardBorder: '#1E2738',
  accent: '#6C63FF',
  green: '#34C759',
  red: '#FF3B30',
  orange: '#FF9500',
  yellow: '#FFD60A',
  text: '#FFFFFF',
  textDim: '#8E8E93',
  textMuted: '#636366',
};

function StatusBadge({ pass, label }) {
  return (
    <View style={[s.badge, { backgroundColor: pass ? COLORS.green + '20' : COLORS.red + '20' }]}>
      <Ionicons name={pass ? 'checkmark-circle' : 'close-circle'} size={14} color={pass ? COLORS.green : COLORS.red} />
      <Text style={[s.badgeText, { color: pass ? COLORS.green : COLORS.red }]}>{label || (pass ? 'PASS' : 'FAIL')}</Text>
    </View>
  );
}

function DeltaBadge({ delta, threshold = 0.5 }) {
  if (delta === null || delta === undefined) return <Text style={s.dimText}>--</Text>;
  const color = delta <= 0.05 ? COLORS.green : delta <= threshold ? COLORS.orange : COLORS.red;
  return (
    <View style={[s.badge, { backgroundColor: color + '20' }]}>
      <Text style={[s.badgeText, { color }]}>{delta.toFixed(3)}%</Text>
    </View>
  );
}

function Card({ title, icon, children, style }) {
  return (
    <View style={[s.card, style]}>
      {title && (
        <View style={s.cardHeader}>
          {icon && <Ionicons name={icon} size={16} color={COLORS.accent} style={{ marginRight: 8 }} />}
          <Text style={s.cardTitle}>{title}</Text>
        </View>
      )}
      {children}
    </View>
  );
}

function MetricBox({ label, value, color, sub }) {
  return (
    <View style={s.metricBox}>
      <Text style={[s.metricValue, { color: color || uiVar('adm-ink', COLORS.text) }]}>{value}</Text>
      <Text style={s.metricLabel}>{label}</Text>
      {sub && <Text style={s.metricSub}>{sub}</Text>}
    </View>
  );
}

export default function MathValidationTab({ embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pastResults, setPastResults] = useState([]);
  const [currentRun, setCurrentRun] = useState(null);
  const [academicResults, setAcademicResults] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [runningAction, setRunningAction] = useState(null);

  // Quick check inputs
  const [qcOdds1, setQcOdds1] = useState('-150');
  const [qcOdds2, setQcOdds2] = useState('+130');
  const [qcResult, setQcResult] = useState(null);
  const [qcLoading, setQcLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${functionsUrl}/mathValidation?action=results&limit=10`);
      const j = await r.json();
      if (j.success) {
        setPastResults(j.results || []);
        if (j.results?.length > 0) setCurrentRun(j.results[0]);
      } else {
        setError(j.error || 'Failed to fetch results');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const runValidation = async (action, label) => {
    setRunningAction(label);
    setError('');
    try {
      const r = await fetch(`${functionsUrl}/mathValidation?action=${action}`, { method: 'GET' });
      const j = await r.json();
      if (j.success) {
        setCurrentRun(j);
        if (action === 'academic') setAcademicResults(j);
        fetchResults();
      } else {
        setError(j.error || `${label} failed`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRunningAction(null);
    }
  };

  const runQuickCheck = async () => {
    setQcLoading(true);
    setQcResult(null);
    try {
      const r = await fetch(`${functionsUrl}/mathValidation?action=quickCheck&odds1=${encodeURIComponent(qcOdds1)}&odds2=${encodeURIComponent(qcOdds2)}`);
      const j = await r.json();
      setQcResult(j);
    } catch (e) {
      setQcResult({ error: e.message });
    } finally {
      setQcLoading(false);
    }
  };

  const summary = currentRun?.summary || null;
  const straights = currentRun?.straights || currentRun;
  const sgps = currentRun?.sgps || null;

  const Wrap = embedded ? View : ScrollView;
  const wrapProps = embedded
    ? { style: [s.container, s.containerEmbedded] }
    : { style: s.container, contentContainerStyle: s.content };

  return (
    <Wrap {...wrapProps}>
      {!embedded && (
        <View style={s.headerRow}>
          <View>
            <Text style={s.title}>Math Validation</Text>
            <Text style={s.subtitle}>EV calculations validated against CNM Devigger API and Clarke et al. (2017) academic paper</Text>
          </View>
        </View>
      )}
      {embedded && (
        <Text style={s.embedHint}>
          Run production math against CNM (live) or Clarke Table 2 (offline). Use Calculator when you just want to poke numbers.
        </Text>
      )}

      {error ? (
        <View style={s.errorBox}>
          <Ionicons name="warning-outline" size={16} color={COLORS.red} />
          <Text style={s.errorText}>{error}</Text>
          <TouchableOpacity onPress={() => setError('')}>
            <Ionicons name="close" size={16} color={COLORS.textDim} />
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Action Buttons */}
      <View style={s.actionsRow}>
        {[
          { action: 'runFull', label: 'Full Validation', icon: 'shield-checkmark-outline', desc: 'Straights + SGPs + Academic' },
          { action: 'runStraights', label: 'Straights Only', icon: 'remove-outline', desc: '20 random bets vs CNM' },
          { action: 'runSGPs', label: 'SGPs Only', icon: 'layers-outline', desc: '10 random SGPs vs CNM' },
          { action: 'academic', label: 'Academic Tests', icon: 'school-outline', desc: 'Clarke paper Table 2' },
        ].map(btn => (
          <TouchableOpacity
            key={btn.action}
            style={[s.actionBtn, runningAction === btn.label && s.actionBtnActive]}
            onPress={() => runValidation(btn.action, btn.label)}
            disabled={!!runningAction}
          >
            {runningAction === btn.label ? (
              <ActivityIndicator size="small" color={COLORS.accent} />
            ) : (
              <Ionicons name={btn.icon} size={20} color={COLORS.accent} />
            )}
            <Text style={s.actionBtnLabel}>{btn.label}</Text>
            <Text style={s.actionBtnDesc}>{btn.desc}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Section Nav */}
      <View style={s.navRow}>
        {['overview', 'straights', 'sgps', 'academic', 'quickCheck', 'history'].map(sec => (
          <TouchableOpacity
            key={sec}
            style={[s.navBtn, activeSection === sec && s.navBtnActive]}
            onPress={() => setActiveSection(sec)}
          >
            <Text style={[s.navBtnText, activeSection === sec && s.navBtnTextActive]}>
              {sec === 'quickCheck' ? 'Quick Check' : sec.charAt(0).toUpperCase() + sec.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {loading && !currentRun ? (
        <View style={s.centered}><ActivityIndicator size="large" color={COLORS.accent} /><Text style={s.dimText}>Loading validation results...</Text></View>
      ) : activeSection === 'overview' ? renderOverview(summary, currentRun) :
        activeSection === 'straights' ? renderStraights(straights) :
        activeSection === 'sgps' ? renderSGPs(sgps) :
        activeSection === 'academic' ? renderAcademic(academicResults || currentRun?.academic) :
        activeSection === 'quickCheck' ? renderQuickCheck(qcOdds1, setQcOdds1, qcOdds2, setQcOdds2, runQuickCheck, qcLoading, qcResult) :
        activeSection === 'history' ? renderHistory(pastResults) :
        null
      }
    </Wrap>
  );
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------

function renderOverview(summary, run) {
  if (!summary && !run) {
    return (
      <Card title="No Validation Data" icon="information-circle-outline">
        <Text style={s.dimText}>Run a validation to see results here. The "Full Validation" button runs all three checks: straights vs CNM, SGPs vs CNM, and academic paper test vectors.</Text>
      </Card>
    );
  }

  const s2 = summary || {};
  return (
    <View>
      <Card title="Validation Summary" icon="shield-checkmark-outline">
        <View style={s.metricsRow}>
          <MetricBox label="Academic" value={s2.academic_pass ? 'PASS' : s2.academic_pass === false ? 'FAIL' : '--'} color={s2.academic_pass ? COLORS.green : s2.academic_pass === false ? COLORS.red : COLORS.textDim} sub="Clarke Table 2" />
          <MetricBox label="Straights Tested" value={s2.straights_tested ?? '--'} color={COLORS.text} sub="vs CNM API" />
          <MetricBox label="Straights Issues" value={s2.straights_discrepancies ?? '--'} color={s2.straights_discrepancies > 0 ? COLORS.orange : COLORS.green} sub={`> 0.5% delta`} />
          <MetricBox label="SGPs Tested" value={s2.sgps_tested ?? '--'} color={COLORS.text} sub="vs CNM correlation" />
          <MetricBox label="SGP Issues" value={s2.sgps_discrepancies ?? '--'} color={s2.sgps_discrepancies > 0 ? COLORS.orange : COLORS.green} sub="> 1.5% delta" />
          <MetricBox label="Errors" value={s2.total_errors ?? '--'} color={s2.total_errors > 0 ? COLORS.red : COLORS.green} sub="API/data errors" />
        </View>
      </Card>

      {run?.timestamp && (
        <Text style={[s.dimText, { textAlign: 'center', marginTop: 8 }]}>Last run: {new Date(run.timestamp).toLocaleString()}</Text>
      )}

      <Card title="What This Validates" icon="help-circle-outline" style={{ marginTop: 16 }}>
        <View style={s.infoGrid}>
          <View style={s.infoItem}>
            <Text style={s.infoTitle}>Straights (vs CNM)</Text>
            <Text style={s.infoDesc}>Pulls random bets from best_bets_cache, reconstructs both-sides odds, and compares our additive/multiplicative/power EV% against CNM's Devigger API. Threshold: 0.5%.</Text>
          </View>
          <View style={s.infoItem}>
            <Text style={s.infoTitle}>SGPs (vs CNM)</Text>
            <Text style={s.infoDesc}>Pulls API-priced 2-leg SGPs from sgp_cache, sends individual leg odds + SGP price to CNM's correlation method. Compares our multi-book devig EV% vs CNM. Threshold: 1.5% (higher due to different vig assumptions).</Text>
          </View>
          <View style={s.infoItem}>
            <Text style={s.infoTitle}>Academic (Clarke 2017)</Text>
            <Text style={s.infoDesc}>Tests our devig formulas against published Table 2 from "Adjusting Bookmaker's Odds to Allow for Overround" — a peer-reviewed paper that defines the additive, multiplicative, and power methods we use.</Text>
          </View>
        </View>
      </Card>
    </View>
  );
}

function renderStraights(data) {
  if (!data?.results?.length) {
    return <Card title="No Straights Data" icon="remove-outline"><Text style={s.dimText}>Run "Straights Only" or "Full Validation" to compare our straight bet EV calculations against CNM.</Text></Card>;
  }

  const results = data.results.filter(r => !r.error);
  const errors = data.results.filter(r => r.error);
  const discrepancies = results.filter(r => r.isDiscrepancy);
  const methodComp = data.perMethodComparison || [];

  return (
    <View>
      <Card title="Straights vs CNM" icon="remove-outline">
        <View style={s.metricsRow}>
          <MetricBox label="Tested" value={data.processed} color={COLORS.text} />
          <MetricBox label="Match" value={results.length - discrepancies.length} color={COLORS.green} sub="< 0.5% delta" />
          <MetricBox label="Discrepancies" value={discrepancies.length} color={discrepancies.length > 0 ? COLORS.orange : COLORS.green} sub="> 0.5% delta" />
          <MetricBox label="Errors" value={data.errors} color={data.errors > 0 ? COLORS.red : COLORS.green} />
        </View>
      </Card>

      {methodComp.length > 0 && (
        <Card title="Per-Method Comparison" icon="git-compare-outline" style={{ marginTop: 12 }}>
          <Text style={[s.dimText, { marginBottom: 8 }]}>Testing each devig method individually against CNM (sample of {Math.min(5, results.length)} bets)</Text>
          <View style={s.tableHeader}>
            <Text style={[s.tableCell, { flex: 2 }]}>Market</Text>
            <Text style={s.tableCell}>Method</Text>
            <Text style={s.tableCell}>Our EV</Text>
            <Text style={s.tableCell}>CNM EV</Text>
            <Text style={s.tableCell}>Delta</Text>
            <Text style={s.tableCell}>Match</Text>
          </View>
          {methodComp.map((mc, i) => (
            <View key={i} style={[s.tableRow, i % 2 === 0 && s.tableRowAlt]}>
              <Text style={[s.tableCellText, { flex: 2 }]} numberOfLines={1}>{mc.selection || mc.market}</Text>
              <Text style={s.tableCellText}>{mc.method}</Text>
              <Text style={s.tableCellText}>{mc.our_ev?.toFixed(2)}%</Text>
              <Text style={s.tableCellText}>{mc.cnm_ev?.toFixed(2)}%</Text>
              <View style={s.tableCell}><DeltaBadge delta={mc.delta} threshold={0.05} /></View>
              <View style={s.tableCell}><StatusBadge pass={mc.match} /></View>
            </View>
          ))}
        </Card>
      )}

      <Card title="Individual Results" icon="list-outline" style={{ marginTop: 12 }}>
        <View style={s.tableHeader}>
          <Text style={[s.tableCell, { flex: 2.5 }]}>Bet</Text>
          <Text style={s.tableCell}>Odds</Text>
          <Text style={s.tableCell}>Our EV</Text>
          <Text style={s.tableCell}>CNM EV</Text>
          <Text style={s.tableCell}>Delta</Text>
        </View>
        {results.map((r, i) => (
          <View key={i} style={[s.tableRow, i % 2 === 0 && s.tableRowAlt, r.isDiscrepancy && s.tableRowWarn]}>
            <View style={[s.tableCell, { flex: 2.5 }]}>
              <Text style={s.tableCellText} numberOfLines={1}>{r.bet?.selection || r.bet?.market}</Text>
              <Text style={s.tableCellSub}>{r.bet?.league} | {r.bet?.sportsbook}</Text>
            </View>
            <Text style={s.tableCellText}>{r.bet?.price}/{r.bet?.opposite}</Text>
            <Text style={s.tableCellText}>{r.our?.ev_worst_case?.toFixed(2) ?? '--'}%</Text>
            <Text style={s.tableCellText}>{r.cnm?.ev_percent?.toFixed(2) ?? '--'}%</Text>
            <View style={s.tableCell}><DeltaBadge delta={r.delta} /></View>
          </View>
        ))}
      </Card>

      {errors.length > 0 && (
        <Card title={`Errors (${errors.length})`} icon="warning-outline" style={{ marginTop: 12 }}>
          {errors.map((e, i) => (
            <Text key={i} style={s.errorItemText}>{e.bet?.selection || 'Unknown'}: {e.error}</Text>
          ))}
        </Card>
      )}
    </View>
  );
}

function renderSGPs(data) {
  if (!data?.results?.length) {
    return <Card title="No SGP Data" icon="layers-outline"><Text style={s.dimText}>Run "SGPs Only" or "Full Validation" to compare our SGP EV calculations against CNM's correlation method.</Text></Card>;
  }

  const results = data.results.filter(r => !r.error);
  const discrepancies = results.filter(r => r.isDiscrepancy);

  return (
    <View>
      <Card title="SGPs vs CNM (Correlation Method)" icon="layers-outline">
        <View style={s.metricsRow}>
          <MetricBox label="Tested" value={data.processed} color={COLORS.text} />
          <MetricBox label="Match" value={results.length - discrepancies.length} color={COLORS.green} sub="< 1.5% delta" />
          <MetricBox label="Discrepancies" value={discrepancies.length} color={discrepancies.length > 0 ? COLORS.orange : COLORS.green} />
          <MetricBox label="Errors" value={data.errors} color={data.errors > 0 ? COLORS.red : COLORS.green} />
        </View>
        <Text style={[s.dimText, { marginTop: 8 }]}>Note: Higher delta threshold (1.5%) because our system and CNM use different SGP vig assumptions. CNM derives vig from the actual market; we use fixed assumptions (2-leg: 12%, 3-leg: 18%).</Text>
      </Card>

      <Card title="Individual SGP Results" icon="list-outline" style={{ marginTop: 12 }}>
        {results.map((r, i) => (
          <View key={i} style={[s.sgpItem, i % 2 === 0 && s.tableRowAlt, r.isDiscrepancy && s.tableRowWarn]}>
            <View style={s.sgpHeader}>
              <Text style={s.sgpFixture}>{r.sgp?.fixture}</Text>
              <Text style={s.sgpLeague}>{r.sgp?.league}</Text>
            </View>
            <View style={s.sgpLegs}>
              {(r.sgp?.legs || []).map((leg, li) => (
                <Text key={li} style={s.sgpLeg}>{leg.player} {leg.market} {leg.selection} ({leg.price > 0 ? '+' : ''}{leg.price})</Text>
              ))}
            </View>
            <View style={s.sgpMetrics}>
              <View style={s.sgpMetric}>
                <Text style={s.sgpMetricLabel}>Combined</Text>
                <Text style={s.sgpMetricValue}>{r.sgp?.combined_price > 0 ? '+' : ''}{r.sgp?.combined_price}</Text>
              </View>
              <View style={s.sgpMetric}>
                <Text style={s.sgpMetricLabel}>Our EV</Text>
                <Text style={[s.sgpMetricValue, { color: (r.our?.ev_percent || 0) >= 0 ? COLORS.green : COLORS.red }]}>
                  {r.our?.ev_percent?.toFixed(2) ?? '--'}%
                </Text>
              </View>
              <View style={s.sgpMetric}>
                <Text style={s.sgpMetricLabel}>CNM EV</Text>
                <Text style={[s.sgpMetricValue, { color: (r.cnm?.ev_percent || 0) >= 0 ? COLORS.green : COLORS.red }]}>
                  {r.cnm?.ev_percent?.toFixed(2) ?? '--'}%
                </Text>
              </View>
              <View style={s.sgpMetric}>
                <Text style={s.sgpMetricLabel}>Delta</Text>
                <DeltaBadge delta={r.delta} threshold={1.5} />
              </View>
              <View style={s.sgpMetric}>
                <Text style={s.sgpMetricLabel}>Quality</Text>
                <Text style={s.sgpMetricValue}>{r.sgp?.devig_quality || '--'}</Text>
              </View>
            </View>
          </View>
        ))}
      </Card>
    </View>
  );
}

function renderAcademic(data) {
  const tests = data?.tests || [];
  if (!tests.length) {
    return <Card title="No Academic Data" icon="school-outline"><Text style={s.dimText}>Run "Academic Tests" to validate our devig formulas against Clarke et al. (2017) Table 2 published results.</Text></Card>;
  }

  return (
    <View>
      <Card title="Clarke et al. (2017) Academic Validation" icon="school-outline">
        <Text style={s.dimText}>Comparing our devig functions against Table 2 from "Adjusting Bookmaker's Odds to Allow for Overround" (American Journal of Sports Science, Vol 5, No 6, pp 45-49).</Text>
        <View style={[s.metricsRow, { marginTop: 12 }]}>
          <MetricBox label="Overall" value={data?.allPass ? 'ALL PASS' : 'ISSUES'} color={data?.allPass ? COLORS.green : COLORS.red} />
        </View>
      </Card>

      {tests.map((test, ti) => (
        <Card key={ti} title={test.test} icon={test.allPass ? 'checkmark-circle-outline' : 'alert-circle-outline'} style={{ marginTop: 12 }}>
          <StatusBadge pass={test.allPass} label={test.allPass ? 'All values match published results' : 'Some values differ from published results'} />

          {test.outcomes ? (
            <View style={{ marginTop: 8 }}>
              <View style={s.tableHeader}>
                <Text style={s.tableCell}>Implied</Text>
                <Text style={s.tableCell}>Expected</Text>
                <Text style={s.tableCell}>Actual</Text>
                <Text style={s.tableCell}>Delta</Text>
                <Text style={s.tableCell}>Status</Text>
              </View>
              {test.outcomes.map((o, i) => (
                <View key={i} style={[s.tableRow, i % 2 === 0 && s.tableRowAlt]}>
                  <Text style={s.tableCellText}>{o.implied}</Text>
                  <Text style={s.tableCellText}>{o.expected}</Text>
                  <Text style={s.tableCellText}>{o.actual}</Text>
                  <Text style={s.tableCellText}>{o.delta?.toFixed(4)}</Text>
                  <View style={s.tableCell}><StatusBadge pass={o.pass} /></View>
                </View>
              ))}
            </View>
          ) : test.outputs ? (
            <View style={{ marginTop: 8 }}>
              <Text style={s.dimText}>Input: {test.input?.odds} (implied: [{test.input?.implied?.join(', ')}], total: {test.input?.totalImplied})</Text>
              {Object.entries(test.outputs).map(([method, vals]) => (
                <View key={method} style={s.academicMethodRow}>
                  <Text style={s.academicMethod}>{method}</Text>
                  <Text style={s.academicValue}>Fair Prob: {vals.fairProb}</Text>
                  <Text style={s.academicValue}>EV: {vals.ev}%</Text>
                </View>
              ))}
            </View>
          ) : null}
        </Card>
      ))}
    </View>
  );
}

function renderQuickCheck(odds1, setOdds1, odds2, setOdds2, runCheck, loading, result) {
  return (
    <View>
      <Card title="Quick Check" icon="flash-outline">
        <Text style={s.dimText}>Enter American odds for both sides of a market to instantly compare our calculation with CNM.</Text>
        <View style={s.qcRow}>
          <View style={s.qcInputWrap}>
            <Text style={s.qcInputLabel}>Side 1 (Your Bet)</Text>
            <TextInput
              style={s.qcInput}
              value={odds1}
              onChangeText={setOdds1}
              placeholder="-150"
              placeholderTextColor={COLORS.textMuted}
              keyboardType="numbers-and-punctuation"
            />
          </View>
          <View style={s.qcInputWrap}>
            <Text style={s.qcInputLabel}>Side 2 (Opposite)</Text>
            <TextInput
              style={s.qcInput}
              value={odds2}
              onChangeText={setOdds2}
              placeholder="+130"
              placeholderTextColor={COLORS.textMuted}
              keyboardType="numbers-and-punctuation"
            />
          </View>
          <TouchableOpacity style={s.qcBtn} onPress={runCheck} disabled={loading}>
            {loading ? <ActivityIndicator size="small" color="#fff" /> : (
              <>
                <Ionicons name="calculator-outline" size={18} color={uiVar('adm-ink', '#fff')} />
                <Text style={s.qcBtnText}>Compare</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </Card>

      {result && !result.error && (
        <Card title="Comparison Result" icon="git-compare-outline" style={{ marginTop: 12 }}>
          <View style={s.qcResultGrid}>
            <View style={s.qcResultCol}>
              <Text style={s.qcResultHeader}>Our System</Text>
              <Text style={s.qcResultLabel}>Additive EV</Text>
              <Text style={s.qcResultValue}>{result.our?.ev_method1_additive?.toFixed(2) ?? '--'}%</Text>
              <Text style={s.qcResultLabel}>Multiplicative EV</Text>
              <Text style={s.qcResultValue}>{result.our?.ev_method2_multiplicative?.toFixed(2) ?? '--'}%</Text>
              <Text style={s.qcResultLabel}>Power EV</Text>
              <Text style={s.qcResultValue}>{result.our?.ev_method3_power?.toFixed(2) ?? '--'}%</Text>
              <Text style={s.qcResultLabel}>Implied Prob</Text>
              <Text style={s.qcResultValue}>{(result.our?.implied_prob * 100)?.toFixed(2) ?? '--'}%</Text>
              <Text style={s.qcResultLabel}>Vig</Text>
              <Text style={s.qcResultValue}>{result.our?.vig_percent ?? '--'}%</Text>
            </View>
            <View style={s.qcResultCol}>
              <Text style={s.qcResultHeader}>CNM Devigger</Text>
              <Text style={s.qcResultLabel}>EV (Worst Case)</Text>
              <Text style={s.qcResultValue}>{result.cnm?.ev_percent != null ? result.cnm.ev_percent.toFixed(2) + '%' : '--'}</Text>
              <Text style={s.qcResultLabel}>Fair Odds</Text>
              <Text style={s.qcResultValue}>{result.cnm?.fair_odds ?? '--'}</Text>
              <Text style={s.qcResultLabel}>Market Juice</Text>
              <Text style={s.qcResultValue}>{result.cnm?.market_juice != null ? result.cnm.market_juice.toFixed(2) + '%' : '--'}</Text>
              <Text style={s.qcResultLabel}>Kelly</Text>
              <Text style={s.qcResultValue}>{result.cnm?.kelly != null ? result.cnm.kelly.toFixed(2) : '--'}</Text>
              <Text style={s.qcResultLabel}>Method</Text>
              <Text style={s.qcResultValue}>{result.cnm?.method ?? '--'}</Text>
            </View>
          </View>
          {result.delta && (
            <View style={[s.qcDeltaRow, { marginTop: 12 }]}>
              <Text style={s.qcDeltaLabel}>Delta (Our Power vs CNM):</Text>
              <DeltaBadge delta={parseFloat(result.delta)} threshold={0.5} />
            </View>
          )}
        </Card>
      )}

      {result?.error && (
        <Card title="Error" icon="warning-outline" style={{ marginTop: 12 }}>
          <Text style={s.errorItemText}>{result.error}</Text>
        </Card>
      )}
    </View>
  );
}

function renderHistory(results) {
  if (!results.length) {
    return <Card title="No History" icon="time-outline"><Text style={s.dimText}>Previous validation runs will appear here.</Text></Card>;
  }

  return (
    <View>
      <Card title="Validation History" icon="time-outline">
        <View style={s.tableHeader}>
          <Text style={[s.tableCell, { flex: 2 }]}>Date</Text>
          <Text style={s.tableCell}>Type</Text>
          <Text style={s.tableCell}>Straights</Text>
          <Text style={s.tableCell}>SGPs</Text>
          <Text style={s.tableCell}>Issues</Text>
        </View>
        {results.map((r, i) => {
          const sum = r.summary || {};
          const ts = r.timestamp || r.created_at?._seconds ? new Date(r.created_at._seconds * 1000).toLocaleString() : '--';
          return (
            <View key={i} style={[s.tableRow, i % 2 === 0 && s.tableRowAlt]}>
              <Text style={[s.tableCellText, { flex: 2 }]}>{typeof ts === 'string' ? ts : new Date(ts).toLocaleString()}</Text>
              <Text style={s.tableCellText}>{r.run_type || r.type || '--'}</Text>
              <Text style={s.tableCellText}>{sum.straights_tested ?? r.processed ?? '--'}</Text>
              <Text style={s.tableCellText}>{sum.sgps_tested ?? '--'}</Text>
              <Text style={[s.tableCellText, { color: (sum.straights_discrepancies || 0) + (sum.sgps_discrepancies || 0) > 0 ? COLORS.orange : COLORS.green }]}>
                {(sum.straights_discrepancies || 0) + (sum.sgps_discrepancies || 0)}
              </Text>
            </View>
          );
        })}
      </Card>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  containerEmbedded: { backgroundColor: 'transparent', flexGrow: 0, flex: 0 },
  content: { padding: 20, paddingBottom: 40 },
  embedHint: { fontSize: 13, color: COLORS.textDim, lineHeight: 19, marginBottom: 16 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), fontSize: 24, fontWeight: '700', color: uiVar('adm-ink', COLORS.text) },
  subtitle: { fontSize: 13, color: COLORS.textDim, marginTop: 4 },

  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  actionBtn: { backgroundColor: COLORS.card, borderWidth: 1, borderColor: COLORS.cardBorder, borderRadius: 12, padding: 16, flex: 1, minWidth: 160, alignItems: 'center', gap: 6 },
  actionBtnActive: { borderColor: COLORS.accent },
  actionBtnLabel: { fontSize: 14, fontWeight: '600', color: uiVar('adm-ink', COLORS.text) },
  actionBtnDesc: { fontSize: 11, color: COLORS.textDim, textAlign: 'center' },

  navRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginBottom: 16, backgroundColor: COLORS.card, borderRadius: 10, padding: 4 },
  navBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  navBtnActive: { backgroundColor: COLORS.accent + '30' },
  navBtnText: { fontSize: 13, color: COLORS.textDim, fontWeight: '500' },
  navBtnTextActive: { color: COLORS.accent },

  card: { backgroundColor: COLORS.card, borderWidth: 1, borderColor: COLORS.cardBorder, borderRadius: 14, padding: 16, marginBottom: 0 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  cardTitle: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), fontSize: 16, fontWeight: '600', color: uiVar('adm-ink', COLORS.text) },

  metricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  metricBox: { backgroundColor: COLORS.bg, borderRadius: 10, padding: 12, flex: 1, minWidth: 100, alignItems: 'center' },
  metricValue: { fontSize: 22, fontWeight: '700' },
  metricLabel: { fontSize: 11, color: COLORS.textDim, marginTop: 4, textAlign: 'center' },
  metricSub: { fontSize: 10, color: COLORS.textMuted, marginTop: 2, textAlign: 'center' },

  tableHeader: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 4, borderBottomWidth: 1, borderBottomColor: COLORS.cardBorder },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 4, alignItems: 'center' },
  tableRowAlt: { backgroundColor: COLORS.bg + '60' },
  tableRowWarn: { backgroundColor: COLORS.orange + '10', borderLeftWidth: 3, borderLeftColor: COLORS.orange },
  tableCell: { flex: 1, paddingHorizontal: 4, justifyContent: 'center' },
  tableCellText: { flex: 1, fontSize: 12, color: uiVar('adm-ink', COLORS.text), paddingHorizontal: 4 },
  tableCellSub: { fontSize: 10, color: COLORS.textDim, paddingHorizontal: 4 },

  badge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, alignSelf: 'flex-start' },
  badgeText: { fontSize: 11, fontWeight: '600' },

  dimText: { fontSize: 13, color: COLORS.textDim, lineHeight: 20 },
  centered: { alignItems: 'center', padding: 40, gap: 12 },

  errorBox: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: COLORS.red + '15', borderRadius: 10, padding: 12, marginBottom: 16 },
  errorText: { flex: 1, fontSize: 13, color: COLORS.red },
  errorItemText: { fontSize: 12, color: COLORS.red, marginBottom: 4 },

  infoGrid: { gap: 12 },
  infoItem: { backgroundColor: COLORS.bg, borderRadius: 8, padding: 12 },
  infoTitle: { fontSize: 14, fontWeight: '600', color: COLORS.accent, marginBottom: 4 },
  infoDesc: { fontSize: 12, color: COLORS.textDim, lineHeight: 18 },

  sgpItem: { padding: 12, borderRadius: 10, marginBottom: 8 },
  sgpHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  sgpFixture: { fontSize: 14, fontWeight: '600', color: uiVar('adm-ink', COLORS.text) },
  sgpLeague: { fontSize: 11, color: COLORS.textDim },
  sgpLegs: { marginBottom: 8 },
  sgpLeg: { fontSize: 12, color: COLORS.textDim, paddingVertical: 2, paddingLeft: 8, borderLeftWidth: 2, borderLeftColor: COLORS.accent + '40', marginBottom: 2 },
  sgpMetrics: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  sgpMetric: { alignItems: 'center', minWidth: 70 },
  sgpMetricLabel: { fontSize: 10, color: COLORS.textMuted },
  sgpMetricValue: { fontSize: 14, fontWeight: '600', color: uiVar('adm-ink', COLORS.text), marginTop: 2 },

  academicMethodRow: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 4, paddingHorizontal: 8 },
  academicMethod: { fontSize: 13, fontWeight: '600', color: COLORS.accent, width: 110 },
  academicValue: { fontSize: 12, color: uiVar('adm-ink', COLORS.text) },

  qcRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 12, alignItems: 'flex-end' },
  qcInputWrap: { flex: 1, minWidth: 120 },
  qcInputLabel: { fontSize: 11, color: COLORS.textDim, marginBottom: 4 },
  qcInput: { backgroundColor: COLORS.bg, borderWidth: 1, borderColor: COLORS.cardBorder, borderRadius: 8, padding: 10, color: uiVar('adm-ink', COLORS.text), fontSize: 16, fontWeight: '600' },
  qcBtn: { backgroundColor: COLORS.accent, borderRadius: 10, paddingHorizontal: 20, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', gap: 6 },
  qcBtnText: { color: uiVar('adm-ink', '#fff'), fontWeight: '600', fontSize: 14 },

  qcResultGrid: { flexDirection: 'row', gap: 16, marginTop: 8 },
  qcResultCol: { flex: 1, backgroundColor: COLORS.bg, borderRadius: 10, padding: 12 },
  qcResultHeader: { fontSize: 14, fontWeight: '700', color: COLORS.accent, marginBottom: 10 },
  qcResultLabel: { fontSize: 11, color: COLORS.textDim, marginTop: 6 },
  qcResultValue: { fontSize: 15, fontWeight: '600', color: uiVar('adm-ink', COLORS.text) },
  qcDeltaRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  qcDeltaLabel: { fontSize: 13, color: COLORS.textDim },
});
